import {
	Button,
	Grid,
	IconButton,
	InputAdornment,
	TextField,
	Typography,
} from "@material-ui/core";
import SearchIcon from "@material-ui/icons/Search";
import {
	GetContextMenuItemsParams,
	GridApi,
	MenuItemDef,
} from "ag-grid-community";
import { AgGridReact } from "ag-grid-react";
import React, { useEffect, useMemo, useState } from "react";
import { useContextMenuMESF } from "../../../../controls";
import { LazyLoading } from "../../../../controls/LazyLoading/LazyLoading";
import { useSettingsContext } from "../context/SettingsContext";
import { Setting } from "../models/Setting";
import { useStyles } from "../styles/useStyles";
import { useSettingsOptionsFunctions } from "./customHooks/useSettingsOptionsFunctions";
import { useTableData } from "./customHooks/useTableSettings";
import { CreateSetting } from "./upsertDelete/CreateSetting";
import { DeleteSetting } from "./upsertDelete/DeleteSetting";
import { EditSetting } from "./upsertDelete/EditSetting";

interface Props {
	rows: Setting[];
	isLoading: boolean;
	getSettingsFromAPI: () => void;
}

export const TableSettings = ({
	getSettingsFromAPI,
	isLoading,
	rows,
}: Props) => {
	const { showContextMenu, registerConfig } = useContextMenuMESF();
	const {
		state,
		actions: { setSettingSelected },
	} = useSettingsContext();
	const classes = useStyles();

	const [gridApi, setGridApi] = useState<GridApi | null>(null);
	const [filterValue, setFilterValue] = useState("");
	const [openModalNew, setOpenModalNew] = useState(false);
	const [openModalEditSetting, setOpenModalEditSetting] = useState(false);
	const [openModalDeleteSetting, setOpenModalDeleteSetting] = useState(false);

	const { getMenuOptions } = useSettingsOptionsFunctions({
		setSettingId: setSettingSelected,
		setShowDeleteModal: setOpenModalDeleteSetting,
		setShowCreateModal: setOpenModalNew,
		setShowEditModal: setOpenModalEditSetting,
	});

	const { columnDefs, defaultColDef } = useTableData({
		setOpenModalEditSetting,
		setOpenModalDeleteSetting,
		showContextMenu,
	});

	const loadingOverlayComponent = () => {
		return (
			<div
				style={{
					width: "100%",
					height: "100%",
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<LazyLoading />
			</div>
		);
	};

	const getContextMenuItems = (
		params: GetContextMenuItemsParams,
	): (string | MenuItemDef)[] => {
		const data = params.node?.data;
		params.api.deselectAll();
		params.node?.setSelected(true);
		showContextMenu(event as any, data, "TableSettings");
		return [];
	};

	useEffect(() => {
		registerConfig({
			id: "TableSettings",
			getOptions: getMenuOptions,
		});
	}, []);

	// hideOverlay() also clears the "no rows" overlay, and this table only mounts once loading is
	// done, so calling it unconditionally hid the empty-state message for good.
	useEffect(() => {
		if (!gridApi) return;
		if (isLoading) {
			gridApi.showLoadingOverlay();
		} else if (rows.length === 0) {
			gridApi.showNoRowsOverlay();
		} else {
			gridApi.hideOverlay();
		}
	}, [isLoading, gridApi, rows.length]);

	return (
		<>
			<Grid
				container
				justifyContent="center"
				alignItems="center"
				className={classes.root}
			>
				<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
					<Typography
						variant="h5"
						style={{ margin: "10px 0 6px", fontWeight: 600 }}
					>
						Settings
					</Typography>
				</Grid>
				<Grid item md={12} xs={12} style={{ padding: "0 15px" }}>
					<TextField
						fullWidth
						label="Search"
						variant="outlined"
						size="small"
						margin="dense"
						value={filterValue}
						onChange={(event) => {
							setFilterValue(event.target.value);
							gridApi?.setQuickFilter(event.target.value);
						}}
						InputProps={{
							startAdornment: (
								<InputAdornment position="start">
									<IconButton edge="start" type="submit">
										<SearchIcon />
									</IconButton>
								</InputAdornment>
							),
						}}
					/>
				</Grid>

				<Grid
					container
					justifyContent="center"
					alignItems="center"
					className={classes.root}
				>
					<Grid
						item
						md={12}
						xs={12}
						style={{ padding: "0 15px" }}
						className={classes.relative}
					>
						<CreateSetting
							show={openModalNew}
							onHide={(shouldUpdate: boolean) => {
								setOpenModalNew(false);
								if (shouldUpdate) getSettingsFromAPI();
							}}
						/>
						<EditSetting
							show={openModalEditSetting}
							onHide={(shouldUpdate: boolean) => {
								setOpenModalEditSetting(false);
								if (shouldUpdate) getSettingsFromAPI();
							}}
						/>
						<DeleteSetting
							show={openModalDeleteSetting}
							onHide={(shouldUpdate: boolean) => {
								setOpenModalDeleteSetting(false);
								if (shouldUpdate) getSettingsFromAPI();
							}}
						/>
						<Grid
							item
							md={12}
							xs={12}
							style={{
								height: "62vh",
								margin: "6px 0 10px",
							}}
						>
							<div
								style={{ height: "100%", width: "100%" }}
								className="ag-theme-balham"
							>
								<AgGridReact
									rowData={rows}
									columnDefs={columnDefs}
									defaultColDef={defaultColDef}
									rowHeight={34}
									headerHeight={34}
									animateRows={true}
									loadingOverlayComponent={loadingOverlayComponent}
									getContextMenuItems={(e: any) => getContextMenuItems(e)}
									statusBar={{
										statusPanels: [
											{
												statusPanel: "agTotalRowCountComponent",
												align: "left",
											},
											{
												statusPanel: "agFilteredRowCountComponent",
												align: "left",
											},
										],
									}}
									rowSelection="single"
									onRowDoubleClicked={(event) => {
										setSettingSelected(event.data as Setting);
										setOpenModalEditSetting(true);
									}}
									onGridReady={(params) => setGridApi(params.api)}
								/>
							</div>
						</Grid>
						<Grid container justifyContent="flex-end">
							<Grid
								item
								md={2}
								xs={12}
								style={{
									display: "flex",
									justifyContent: "flex-end",
								}}
							>
								<Button
									variant="contained"
									color="primary"
									onClick={() => setOpenModalNew(!openModalNew)}
									fullWidth
									style={{ marginTop: 10 }}
								>
									NEW SETTING
								</Button>
							</Grid>
						</Grid>
					</Grid>
				</Grid>
			</Grid>
		</>
	);
};
